import { createReducer, INITIAL_STATE } from '@shared/redux';
import { LOAD_STATE } from '@shared/axios';
import { createAPIConfigForUpdateTagGroup } from '@domain/dashboard/budget-manager/budget-manager.api';
import { BBMConfig, TagGroupPayload } from '@domain/dashboard/budget-manager';
import { AxiosActionPayload, AxiosResponseAction } from '@shared/axios/axios.types';
import { createCluster, editCluster } from './table/components/cluster-selector/cluster-selector.utils';

export const BUDGET_MANAGER_CAMPAIGNS_MODAL_OPENED = '@budget-manager/modal/BUDGET_MANAGER_CAMPAIGNS_MODAL_OPENED';
export const BUDGET_MANAGER_CAMPAIGNS_MODAL_CLOSED = '@budget-manager/modal/BUDGET_MANAGER_CAMPAIGNS_MODAL_CLOSED';
export const SET_FILTERS = '@budget-manager/modal/SET_FILTERS';

export const SET_MODAL_DRAFT_CLUSTERS = '@budget-manager/clusters/SET_MODAL_DRAFT_CLUSTERS';
export const EDIT_DRAFT_CLUSTER = '@budget-manager/clusters/EDIT_DRAFT_CLUSTER';
export const CREATE_DRAFT_CLUSTER = '@budget-manager/clusters/CREATE_DRAFT_CLUSTER';

export const SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_CALLED = '@budget-manager/clusters/SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_CALLED';
export const SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_SUCCEEDED = '@budget-manager/clusters/SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_SUCCEEDED';
export const SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_FAILED = '@budget-manager/clusters/SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_FAILED';


export type SaveBudgetManagerClustersRequestSucceeded = AxiosResponseAction<
    typeof SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_SUCCEEDED,
    TagGroupPayload,
    AxiosActionPayload<ReturnType<typeof saveBudgetManagerClusters>>
    >;

export type SaveBudgetManagerClustersRequestFailed = AxiosResponseAction<
    typeof SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_FAILED,
    TagGroupPayload,
    AxiosActionPayload<ReturnType<typeof saveBudgetManagerClusters>>
    >;

export const getBudgetManagerClustersLoadState = state => state
    .budgetManager?.modal?.loadState;

export const getBudgetManagerClustersGroup = state => state
    .budgetManager?.modal?.budgetManagerClustersGroup;

export const getDraftClusters = state => state
    .budgetManager?.modal?.draftClusters;

export const getFilters = state => state
    .budgetManager?.modal?.filters;

export const
    isBudgetManagerCampaignsModalOpen = state => state.budgetManager?.modal?.isModalOpen;

export const setModalDraftClusters = clusters => ({
    type: SET_MODAL_DRAFT_CLUSTERS,
    payload: {
        clusters,
    },
});

export const saveBudgetManagerClusters = ({
    clientId,
    groupId,
    objects,
    isFbTaggedLevelAdset = false,
}: BBMConfig) => ({
    type: SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_CALLED,
    actionTypes: {
        success: SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_SUCCEEDED,
        fail: SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_FAILED,
    },
    payload: { clientId, groupId, objects },
    config: createAPIConfigForUpdateTagGroup({
        clientId,
        groupId,
        isFbTaggedLevelAdset,
        name: 'Budget Manager Automation',
        platform: 'client',
        taggedCampaigns: objects,
    }),
});

export const editDraftCluster = ({ campaign, newCluster }) => ({
    type: EDIT_DRAFT_CLUSTER,
    payload: {
        campaign,
        newCluster,
    },
});

export const createDraftCluster = ({
    campaign, clusterName, organizationId, clientId,
}) => ({
    type: CREATE_DRAFT_CLUSTER,
    payload: {
        campaign,
        clusterName,
        organizationId,
        clientId,
    },
});

export const openBudgetManagerCampaignsModal = () => ({
    type: BUDGET_MANAGER_CAMPAIGNS_MODAL_OPENED,
    payload: {},
});

export const closeBudgetManagerCampaignsModal = () => ({
    type: BUDGET_MANAGER_CAMPAIGNS_MODAL_CLOSED,
    payload: {},
});

export const setFilters = values => ({
    type: SET_FILTERS,
    payload: values,
});

const initialState = {
    draftClusters: [],
    isModalOpen: false,
    loadState: LOAD_STATE.LOADING,
    error: null,
    filters: {},
};

export const budgetManagerCampaignsModalReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [BUDGET_MANAGER_CAMPAIGNS_MODAL_OPENED]: state => ({
        ...state,
        isModalOpen: true,
    }),
    [BUDGET_MANAGER_CAMPAIGNS_MODAL_CLOSED]: state => ({
        ...state,
        isModalOpen: false,
    }),
    [SET_MODAL_DRAFT_CLUSTERS]: (state, action) => ({
        ...state,
        draftClusters: action.payload.clusters,
        loadState: LOAD_STATE.SUCCESS,
    }),
    [SET_FILTERS]: (state, action) => ({
        ...state,
        filters: action.payload,
    }),
    [SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_CALLED]: state => ({
        ...state,
        loadState: LOAD_STATE.LOADING,
        error: null,
    }),
    [SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_SUCCEEDED]: state => ({
        ...state,
        loadState: LOAD_STATE.SUCCESS,
        error: null,
    }),
    [SAVE_BUDGET_MANAGER_CLUSTERS_REQUEST_FAILED]: (
        state,
        action: SaveBudgetManagerClustersRequestFailed,
    ) => ({
        ...state,
        loadState: LOAD_STATE.FAIL,
        error: action.payload,
    }),
    [EDIT_DRAFT_CLUSTER]: (state, action) => ({
        ...state,
        draftClusters: editCluster({
            draftClusters: state.draftClusters,
            ...action.payload,
        }),
        error: null,
    }),
    [CREATE_DRAFT_CLUSTER]: (state, action) => ({
        ...state,
        draftClusters: createCluster({
            draftClusters: state.draftClusters,
            ...action.payload,
        }),
        error: null,
    }),
});
